/**
 * ELM327 Web Bluetooth transport. Owns the GATT connection, command
 * serialization, and response framing. Protocol encode/decode lives in
 * `@tendedloop/shared` (obd2); this file is the radio.
 *
 * NOTE: Web Bluetooth is unsupported on iOS Safari. Callers must gate on
 * `isWebBluetoothAvailable()` and surface the Bluefy / Android guidance.
 */

import { ELM327_TERMINATOR } from '@tendedloop/shared';

/**
 * Candidate GATT service UUIDs across common ELM327 BLE modules. Declared as
 * optionalServices so the browser grants access after the user picks a device;
 * the actual characteristics are auto-discovered (notify + write) rather than
 * hardcoded, since clones vary.
 */
const KNOWN_OBD_SERVICES: string[] = [
  '0000fff0-0000-1000-8000-00805f9b34fb', // common clone (FFF0)
  '0000ffe0-0000-1000-8000-00805f9b34fb', // HM-10 style (FFE0)
  '0000ffe5-0000-1000-8000-00805f9b34fb',
  '6e400001-b5a3-f393-e0a9-e50e24dcca9e', // Nordic UART Service (some OBDLink/clones)
  '00001101-0000-1000-8000-00805f9b34fb', // SPP-style
];

const ENCODER = new TextEncoder();
const DECODER = new TextDecoder();

export type TransportLogLevel = 'tx' | 'rx' | 'info' | 'error';
export type TransportLogger = (level: TransportLogLevel, message: string) => void;

export class Elm327Transport {
  private device: BluetoothDevice | null = null;
  private write: BluetoothRemoteGATTCharacteristic | null = null;
  private notify: BluetoothRemoteGATTCharacteristic | null = null;
  private buffer = '';
  private pending: { resolve: (raw: string) => void; reject: (err: Error) => void; timer: number } | null = null;
  /** Serializes commands — the ELM327 handles one request at a time. */
  private queue: Promise<unknown> = Promise.resolve();
  private log: TransportLogger;
  private onDisconnect?: () => void;

  constructor(opts: { log?: TransportLogger; onDisconnect?: () => void } = {}) {
    this.log = opts.log ?? (() => {});
    this.onDisconnect = opts.onDisconnect;
  }

  get deviceName(): string | undefined {
    return this.device?.name;
  }

  get connected(): boolean {
    return Boolean(this.device?.gatt?.connected && this.write && this.notify);
  }

  /** Prompt the chooser, connect GATT, discover characteristics, enable notifications. */
  async connect(): Promise<void> {
    if (!navigator.bluetooth) throw new Error('Web Bluetooth not available');

    this.device = await navigator.bluetooth.requestDevice({
      // We can't know the exact device name, so accept all and rely on the
      // user picking their adapter; optionalServices unlocks GATT access.
      acceptAllDevices: true,
      optionalServices: KNOWN_OBD_SERVICES,
    });

    this.device.addEventListener('gattserverdisconnected', () => {
      this.log('info', 'GATT disconnected');
      this.write = null;
      this.notify = null;
      this.onDisconnect?.();
    });

    const server = await this.device.gatt!.connect();
    const { write, notify } = await this.discoverCharacteristics(server);
    this.write = write;
    this.notify = notify;

    await this.notify.startNotifications();
    this.notify.addEventListener('characteristicvaluechanged', this.handleNotification);
    this.log('info', `Connected to ${this.device.name ?? 'OBD adapter'}`);
  }

  /**
   * Auto-discover a notify characteristic and a writable characteristic across
   * all primary services. Robust to the UUID variety of cheap clones.
   */
  private async discoverCharacteristics(
    server: BluetoothRemoteGATTServer,
  ): Promise<{ write: BluetoothRemoteGATTCharacteristic; notify: BluetoothRemoteGATTCharacteristic }> {
    const services = await server.getPrimaryServices();
    let write: BluetoothRemoteGATTCharacteristic | null = null;
    let notify: BluetoothRemoteGATTCharacteristic | null = null;

    for (const service of services) {
      const chars = await service.getCharacteristics();
      for (const c of chars) {
        if (!notify && c.properties.notify) notify = c;
        if (!write && (c.properties.write || c.properties.writeWithoutResponse)) write = c;
      }
      if (write && notify) break;
    }

    if (!write || !notify) {
      throw new Error('No compatible OBD characteristics found on this adapter');
    }
    return { write, notify };
  }

  private handleNotification = (event: Event): void => {
    const target = event.target as BluetoothRemoteGATTCharacteristic;
    const value = target.value;
    if (!value) return;
    const chunk = DECODER.decode(value);
    this.buffer += chunk;
    // ELM327 terminates every reply with the '>' prompt.
    if (this.buffer.includes('>')) {
      const raw = this.buffer;
      this.buffer = '';
      const p = this.pending;
      this.pending = null;
      if (p) {
        window.clearTimeout(p.timer);
        this.log('rx', raw.replace(/[\r\n]+/g, ' ').trim());
        p.resolve(raw);
      }
    }
  };

  /** Send one ELM327 command and resolve with its raw reply (queued + timed out). */
  sendCommand(cmd: string, timeoutMs = 5000): Promise<string> {
    const run = async (): Promise<string> => {
      if (!this.write) throw new Error('Not connected');
      this.buffer = '';
      const payload = ENCODER.encode(cmd + ELM327_TERMINATOR);
      this.log('tx', cmd);
      return new Promise<string>((resolve, reject) => {
        const timer = window.setTimeout(() => {
          this.pending = null;
          reject(new Error(`OBD command "${cmd}" timed out`));
        }, timeoutMs);
        this.pending = { resolve, reject, timer };
        // Prefer write-without-response when available (ELM327 BLE modules
        // commonly only support that); fall back to writeValue.
        const w = this.write!;
        const writer =
          w.properties.writeWithoutResponse && w.writeValueWithoutResponse
            ? w.writeValueWithoutResponse.bind(w)
            : w.writeValue.bind(w);
        writer(payload).catch((err: unknown) => {
          window.clearTimeout(timer);
          this.pending = null;
          reject(err instanceof Error ? err : new Error(String(err)));
        });
      });
    };

    // Chain onto the queue so commands never overlap on the wire.
    const next = this.queue.then(run, run);
    this.queue = next.catch(() => undefined);
    return next;
  }

  disconnect(): void {
    try {
      if (this.notify) this.notify.removeEventListener('characteristicvaluechanged', this.handleNotification);
      this.device?.gatt?.disconnect();
    } catch {
      /* best effort */
    }
    this.write = null;
    this.notify = null;
  }
}

/** True only where the platform actually exposes Web Bluetooth (not iOS Safari). */
export function isWebBluetoothAvailable(): boolean {
  return typeof navigator !== 'undefined' && Boolean(navigator.bluetooth);
}
